import React, { createContext, useState, useContext, useCallback } from 'react';

import { isAfter, isBefore, min } from 'date-fns';
import IProduct from '~/interfaces/IProduct';
import heroku from '~/services/heroku';
import { bff } from '~/services/bff';
import IMenu from '~/interfaces/IMenu';

interface ProductsContextData {
  products: IProduct[];
  loadProducts(): Promise<void>;
  loadProductsHeroku(): Promise<void>;
  clearContext(): void;
  isInitialized: boolean;
}

const ProductsContext = createContext<ProductsContextData>(
  {} as ProductsContextData,
);

const { REACT_APP_FIREBASE_PROJECT } = process.env;

export const ProductsProvider: React.FC = ({ children }) => {
  const [products, setProducts] = useState<IProduct[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const clearContext = useCallback(() => {
    setProducts([]);
  }, []);

  const loadProductsHeroku = useCallback(async () => {
    const response = await heroku.get('');
    setProducts(response.data);
    setIsInitialized(true);
  }, []);

  const loadProducts = useCallback(async () => {
    const response = await bff.get(`/app/${REACT_APP_FIREBASE_PROJECT}`);

    const menus = response.data.menus as IMenu[];
    let menuProducts = response.data.products as IProduct[];

    if (menus.length > 0) {
      const referenceDate = new Date();
      const menu = menus.find((item: IMenu) => {
        const [h1, m1] = item.initHour.split(':');
        const [h2, m2] = item.endHour.split(':');
        const initDate = new Date();
        initDate.setHours(parseInt(h1), parseInt(m1), 0);
        const endDate = new Date();
        endDate.setHours(parseInt(h2), parseInt(m2), 0);

        if (
          item.status &&
          isAfter(referenceDate, initDate) &&
          isBefore(referenceDate, endDate) &&
          item.weekDays.includes(`${initDate.getDay()}`)
        ) {
          return item;
        }
      });

      if (menu) {
        menuProducts = menuProducts.filter(item =>
          menu.productIds.includes(item.uid),
        );
      } else {
        menuProducts = [];
      }
    }

    setProducts(menuProducts);
    setIsInitialized(true);
  }, []);

  return (
    <ProductsContext.Provider
      value={{
        products,
        loadProducts,
        clearContext,
        isInitialized,
        loadProductsHeroku,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export function useProducts(): ProductsContextData {
  const context = useContext(ProductsContext);
  return context;
}
